import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { store } from "@app/store";
import { s3Key } from "@app/hooks/s3";
import { videoKey } from "@app/hooks/videos";
import { useStable } from "@app/hooks/useStable";
import { useUpdateAssets, useUploadAssets } from "@app/hooks/videoWizard";
import { generateImageFn } from "@app/hooks/firefly";
import { removeBackgroundFn } from "@app/services/photoshop";
import {
  filterActiveVariations,
  getPrimaryReferenceKey,
  getVariationReferenceKeys,
  getReferenceGroupId,
  isVariationEnabled,
  VARIATION_PROGRESS_STAGE,
} from "@app/views/projects/wizardSteps";

import {
  getCampaignChangedVariationKeys,
  getVariationDependencies,
  isUploadedImage,
  snapshotDependencies,
  snapshotReferences,
} from "@app/views/projects/variationDependencies";

import { resolveAsset, resolveReferenceAssets } from "./resolve";
import {
  buildVariationPayload,
  compileUserPrompt,
  getVariationPromptTemplate,
  isStaticPrompt,
  resolveStaticPrompt,
  resolveVariationUploadIds,
} from "./variationRequest";
import { useImageLabeling } from "./useImageLabeling";
import { useImagePrompts } from "./useImagePrompts";
import { useImageReferenceUploads } from "./useImageReferenceUploads";
import { usePersistImageVariations } from "./usePersistImageVariations";

/**
 * Image variation generation for a set of template stages.
 *
 * Owns the whole pipeline of a wizard step: the reference images are described
 * (`useImageLabeling`) and uploaded (`useImageReferenceUploads`), a
 * `visualPrompt` is generated for every variation that lacks one
 * (`useImagePrompts`), and each variation is then generated — through Firefly,
 * or through Photoshop for the non-generative actions — and persisted as an
 * asset named after the variation.
 *
 * A static prompt (see `resolveStaticPrompt`) is recomputed from the campaign
 * as it is whenever its image is generated, so it never falls behind it —
 * unless the user wrote it (`promptEdited`), which is kept as written.
 *
 * The pipeline is *armed* rather than started: `run()` turns it on, and it
 * then keeps reacting to the video document, so clearing a variation's asset
 * or prompt (as the edit dialog does) regenerates it. Because generation is
 * asynchronous and multi-step, the caller renders its own progress — see
 * `progressByKey` and `generatingKeys`.
 *
 * Reusable from any wizard step: the step decides which `stages` it owns, and
 * gets back the reference groups to render.
 *
 * @param {object} options
 * @param {string} options.projectId - The video/project to generate for.
 * @param {object} options.video - The video query (`data`, `isSuccess`, …).
 * @param {object} options.templateData - The resolved template document.
 * @param {Array<string>} [options.stages] - Image stages this step owns.
 *   Nullish means "every stage".
 * @param {Array<string>} [options.keys] - Variation keys this step owns,
 *   memoized. When given, it replaces the `stages` filter — a step can own
 *   variations by key regardless of the stage they belong to.
 * @param {Map<string, object>} [options.sourceDependencies] - What the step's
 *   campaign structure adds to the campaign keys a variation depends on, by
 *   variation key (see `getStructureDependencies`), memoized. A generated
 *   image records the values of the keys it depends on as its `sourceValue`.
 * @param {object} [options.selectedCampaign] - Campaign the prompts are
 *   written for; also gates `condition`-ed variations.
 * @param {boolean} [options.enabled] - Whether the pipeline may run at all.
 * @param {boolean} [options.manual] - Whether a variation that was never
 *   generated waits to be asked for (`generate()`) rather than being generated
 *   as soon as the pipeline is armed. A variation that already has an image or
 *   a persisted record is handled as usual, so regenerating it still works.
 */
export const useImageVariations = ({
  projectId,
  video,
  templateData,
  stages,
  keys,
  sourceDependencies,
  selectedCampaign,
  enabled = true,
  manual = false,
}) => {
  // Armed per project: `run()` turns the pipeline on, switching projects turns
  // it back off — a step action of the previous project must not keep
  // generating into the next one.
  const [armedProjectId, setArmedProjectId] = useState(null);

  // A forced run regenerates variations that already have an image — the
  // keys it was asked for, per project. Each key is consumed once its
  // generation has started; until then, it keeps the references it needs
  // uploading. `runToken` is what wakes the generation effect up for a run.
  const [forced, setForced] = useState(() => ({
    projectId: null,
    keys: new Set(),
  }));
  const [runToken, setRunToken] = useState(0);

  const isArmed = !!projectId && armedProjectId === projectId;
  const isActive = !!enabled && isArmed;

  const hasTemplateData = !!templateData;

  const videoData = video?.data;

  // Only the variations belonging to this step's stages — and enabled for the
  // current campaign — are generated here. Later stages stay idle until their
  // own step is reached, and a variation gated by a `condition` never
  // generates when the flag is off.
  const stageVariations = useMemo(() => {
    if (keys) {
      const owned = new Set(keys);

      return (templateData?.imageVariations ?? []).filter(
        (variation) =>
          owned.has(variation.key) &&
          isVariationEnabled(variation, selectedCampaign),
      );
    }

    return filterActiveVariations(
      templateData?.imageVariations,
      stages,
      selectedCampaign,
    );
  }, [templateData?.imageVariations, stages, keys, selectedCampaign]);

  const stageKeys = useMemo(
    () => new Set(stageVariations.map((variation) => variation.key)),
    [stageVariations],
  );

  // The keys a forced run still has to regenerate, for the current project.
  const forcedKeys = useMemo(
    () => (forced.projectId === projectId ? forced.keys : new Set()),
    [forced, projectId],
  );

  // The never-generated variations asked for with `generate()`, per project —
  // only consulted in `manual` mode.
  const [requested, setRequested] = useState(() => ({
    projectId: null,
    keys: new Set(),
  }));

  // The variations the pipeline works on: every one of the step's, unless it is
  // `manual`, where a variation without a persisted record — never generated,
  // or only uploaded — waits until it is asked for.
  const eligibleVariations = useStable(
    useMemo(() => {
      if (!manual) {
        return stageVariations;
      }

      const requestedKeys =
        requested.projectId === projectId ? requested.keys : null;

      return stageVariations.filter(
        (variation) =>
          !!requestedKeys?.has(variation.key) ||
          !!videoData?.imageVariations?.some?.((v) => v.key === variation.key),
      );
    }, [
      manual,
      stageVariations,
      requested,
      projectId,
      videoData?.imageVariations,
    ]),
  );

  const eligibleKeys = useMemo(
    () => new Set(eligibleVariations.map((variation) => variation.key)),
    [eligibleVariations],
  );

  /**
   * The step's variations still waiting to be asked for (`manual` only): not
   * in the pipeline, and without an image.
   */
  const idleKeys = useMemo(
    () =>
      new Set(
        stageVariations
          .filter(
            (variation) =>
              !eligibleKeys.has(variation.key) &&
              !videoData?.assets?.some((asset) => asset.name === variation.key),
          )
          .map((variation) => variation.key),
      ),
    [stageVariations, eligibleKeys, videoData?.assets],
  );

  // Bound to the current assets, so the memos and effects below keep a stable
  // identity for as long as the assets themselves do.
  const getAsset = useCallback(
    (key) => resolveAsset(videoData?.assets, key),
    [videoData?.assets],
  );

  /** A variation's reference images, in order, primary first. */
  const getReferenceAssets = useCallback(
    (variation) => resolveReferenceAssets(videoData?.assets, variation),
    [videoData?.assets],
  );

  /**
   * Whether a reference key points at an image that exists or is still going
   * to be generated — the ones worth waiting for before generating from them.
   */
  const isReferenceExpected = useCallback(
    (key) =>
      !!key &&
      (!!videoData?.assets?.some((asset) => asset.name === key) ||
        !!templateData?.imageVariations?.some(
          (variation) => variation.key === key,
        )),
    [videoData?.assets, templateData?.imageVariations],
  );

  // Variations are grouped by their whole `referenceKey`: every variation
  // built from exactly the same references, in the same order, shares a group,
  // and `["IMG1", "IMG2"]` is a group of its own rather than part of `"IMG1"`.
  const groups = useStable(
    useMemo(() => {
      const byId = new Map();

      stageVariations.forEach((variation) => {
        const referenceKeys = getVariationReferenceKeys(variation);
        const id = getReferenceGroupId(referenceKeys);

        if (!byId.has(id)) {
          byId.set(id, {
            id,
            referenceKeys,
            referenceAssets: referenceKeys.map(getAsset),
            // Whether the group is generated from an image that exists or is
            // still going to be generated — i.e. whether there is a reference
            // worth rendering and waiting for.
            hasReference: referenceKeys.some(isReferenceExpected),
            keys: [],
            variations: [],
          });
        }

        const group = byId.get(id);

        group.keys.push(variation.key);
        group.variations.push({
          ...variation,
          // The whole group's references, so the edit dialog can resolve them
          // without re-reading the template.
          referenceKey:
            referenceKeys.length > 1 ? referenceKeys : referenceKeys[0],
          data:
            videoData?.imageVariations?.find?.(
              (v) => v.key === variation.key,
            ) ?? {},
        });
      });

      return Array.from(byId.values());
    }, [
      stageVariations,
      getAsset,
      isReferenceExpected,
      videoData?.imageVariations,
    ]),
  );

  // Every reference image of this step, de-duplicated — the set that is
  // presigned, described and uploaded once for all groups.
  const referenceAssets = useStable(
    useMemo(() => {
      const byName = new Map();

      groups.forEach((group) => {
        group.referenceAssets.forEach((asset) => {
          if (asset?.name && !byName.has(asset.name)) {
            byName.set(asset.name, asset);
          }
        });
      });

      return Array.from(byName.values());
    }, [groups]),
  );

  // The reference images the pipeline actually works on — those of the groups
  // with an eligible variation; every one of them unless `manual`.
  const eligibleReferenceAssets = useStable(
    useMemo(() => {
      const byName = new Map();

      groups
        .filter((group) => group.keys.some((key) => eligibleKeys.has(key)))
        .forEach((group) => {
          group.referenceAssets.forEach((asset) => {
            if (asset?.name && !byName.has(asset.name)) {
              byName.set(asset.name, asset);
            }
          });
        });

      return Array.from(byName.values());
    }, [groups, eligibleKeys]),
  );

  // Wrapped so the empty fallback keeps a stable identity across renders —
  // `campaign` is an effect dependency further down.
  const campaign = useMemo(() => selectedCampaign ?? {}, [selectedCampaign]);

  const writer = usePersistImageVariations(projectId);
  const persistImageVariations = writer.persist;

  const updateAssets = useUpdateAssets(projectId);
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------------
  // Generation bookkeeping
  //
  // The keys being generated are rendered by the caller, so they are state —
  // mirrored into a ref because the generation effect reads them while
  // deciding what is still left to do.
  // ---------------------------------------------------------------------------

  const [generatingKeys, setGeneratingKeys] = useState(() => new Set());
  const generatingKeysRef = useRef(generatingKeys);

  const startGenerating = useCallback((key) => {
    if (!key || generatingKeysRef.current.has(key)) {
      return;
    }

    const next = new Set(generatingKeysRef.current);
    next.add(key);
    generatingKeysRef.current = next;
    setGeneratingKeys(next);
  }, []);

  const stopGenerating = useCallback((key) => {
    if (!key || !generatingKeysRef.current.has(key)) {
      return;
    }

    const next = new Set(generatingKeysRef.current);
    next.delete(key);
    generatingKeysRef.current = next;
    setGeneratingKeys(next);
  }, []);

  const uploadAssets = useUploadAssets(projectId, {
    onSettled: (data, error, variables) => {
      if (variables.key) {
        store.getState().setImageVariationProgress(variables.key, null);
        stopGenerating(variables.key);
      }
    },
  });

  // ---------------------------------------------------------------------------
  // Middle steps — each usable on its own
  // ---------------------------------------------------------------------------

  // Uploading a reference is only worth it for a variation that is about to be
  // generated — missing its image (and not failed, which only regenerates when
  // asked for) or forced. The references of the step's other variations are
  // displayed, but not uploaded.
  const uploadKeys = useMemo(() => {
    const needsGeneration = (variation) =>
      eligibleKeys.has(variation.key) &&
      (forcedKeys.has(variation.key) ||
        (!videoData?.assets?.some((asset) => asset.name === variation.key) &&
          !videoData?.imageVariations?.some?.(
            (v) => v.key === variation.key && !!v.errorCode,
          )));

    const names = new Set();

    groups
      .filter((group) => group.variations.some(needsGeneration))
      .forEach((group) => {
        group.referenceAssets.forEach((asset) => {
          if (asset?.name) {
            names.add(asset.name);
          }
        });
      });

    return names;
  }, [
    groups,
    eligibleKeys,
    forcedKeys,
    videoData?.assets,
    videoData?.imageVariations,
  ]);

  // When a group's references cannot be uploaded there is nothing to generate
  // from, so its variations fall back to the group's primary reference image.
  const referenceFallbacks = useStable(
    useMemo(
      () =>
        groups.flatMap((group) =>
          group.variations
            .filter(
              (variation) =>
                eligibleKeys.has(variation.key) &&
                !videoData?.assets?.some(
                  (asset) => asset.name === variation.key,
                ),
            )
            .map((variation) => ({
              key: variation.key,
              asset: group.referenceAssets[0],
            })),
        ),
      [groups, eligibleKeys, videoData?.assets],
    ),
  );

  const references = useImageReferenceUploads({
    projectId,
    assets: eligibleReferenceAssets,
    fallbacks: referenceFallbacks,
    uploadKeys,
    enabled: isActive && uploadKeys.size > 0,
  });

  const needsImageLabeling = useMemo(
    () =>
      eligibleVariations.some((variation) => {
        const visuals = getReferenceAssets(variation);
        return (
          variation?.imageDescriptionPrompt === "brand-visibility-library" ||
          !isStaticPrompt(variation, {
            templateId: videoData?.templateId,
            campaign,
            customer: videoData?.customer,
            visual: visuals[0],
            visuals,
          })
        );
      }),
    [
      eligibleVariations,
      getReferenceAssets,
      videoData?.templateId,
      campaign,
      videoData?.customer,
    ],
  );

  const labeling = useImageLabeling({
    projectId,
    assets: eligibleReferenceAssets,
    urlByPath: references.urlByPath,
    templateId: videoData?.templateId,
    imageVariations: templateData?.imageVariations,
    enabled: isActive && needsImageLabeling,
    companyName: selectedCampaign?.["Company-name"],
    product: selectedCampaign?.["Campaign-product"],
  });
  const { retry: retryLabeling } = labeling;

  const prompts = useImagePrompts({
    projectId,
    variations: eligibleVariations,
    imageVariations: videoData?.imageVariations,
    templateId: videoData?.templateId,
    campaign,
    customer: videoData?.customer,
    getReferenceAssets,
    isLabeled: labeling.isLabeled,
    persist: persistImageVariations,
    enabled: isActive && !!video?.isSuccess && hasTemplateData,
  });

  // ---------------------------------------------------------------------------
  // Generation
  // ---------------------------------------------------------------------------

  const imageVariation = useMutation({
    // The snapshots are only recorded on the result, never sent along.
    mutationFn: (
      { key, action, sourceValue, sourceReferences, ...modelOptions },
      { signal },
    ) => {
      // Non-generative Photoshop actions take a different route, but return the
      // same `{ success, outputs }` shape so `onSuccess` below is shared.
      if (action === "removeBackground") {
        const assetName = `photoshop__${key}__${crypto.randomUUID()}`;
        const s3FilePath = videoData?.projectPath
          ? `${videoData.projectPath}/${assetName}`
          : undefined;

        return removeBackgroundFn(
          { ...modelOptions, s3FilePath },
          signal,
          () => {
            store.getState().setImageVariationProgress(key, (prev) => ({
              stage: VARIATION_PROGRESS_STAGE.GENERATING,
              value: prev?.value ?? 0,
              label: "Removing background",
            }));
          },
        ).then((result) => ({ ...result, assetName }));
      }

      const assetName = `firefly__${key}__${crypto.randomUUID()}`;
      const s3FilePath = videoData?.projectPath
        ? `${videoData.projectPath}/${assetName}`
        : undefined;

      return generateImageFn(
        { ...modelOptions, s3FilePath },
        signal,
        (status) => {
          if (status.progress) {
            store.getState().setImageVariationProgress(key, (prev) => ({
              stage: VARIATION_PROGRESS_STAGE.GENERATING,
              value: status.progress,
              label: `Generating ${modelOptions.model?.startsWith("video") ? "video" : "image"}`,
            }));
          } else {
            store.getState().setImageVariationProgress(key, (prev) => ({
              stage: VARIATION_PROGRESS_STAGE.GENERATING,
              value: prev?.value ?? 0,
              label: `Generating ${modelOptions.model?.startsWith("video") ? "video" : "image"}`,
            }));
          }
        },
      ).then((result) => ({ ...result, assetName }));
    },
    onMutate: (variables) => {
      const { key } = variables;

      startGenerating(key);
    },
    onSuccess: (data, variables) => {
      const {
        key,
        model,
        prompt,
        referenceKey,
        action,
        sourceValue,
        sourceReferences,
      } = variables;
      const isVideoModel = model?.startsWith("video");

      const outs = data?.outputs ?? [];
      const assetData = isVideoModel
        ? (outs.find((o) => o.kind === "video") ??
          outs.find(
            (o) =>
              typeof o.url === "string" &&
              /\.(mp4|webm|mov)(\?|$)/i.test(o.url),
          ) ??
          outs.at(-1) ??
          {})
        : (outs.find((o) => o.kind === "image") ?? outs[0] ?? {});
      let fireflyId = assetData.fireflyId;

      if (!fireflyId) {
        // Try to extract Firefly ID from the URL
        const match = assetData.url?.match(
          /\/(?:images|videos)\/([a-f0-9-]{36})(?:[\/\?])/,
        );
        if (match) {
          fireflyId = match[1];
        }
      }

      store.getState().setImageVariationProgress(key, {
        stage: VARIATION_PROGRESS_STAGE.SAVING,
        value: 0,
        label: `Saving ${isVideoModel ? "video" : "image"}`,
      });

      let assetName = data?.assetName;

      if (!assetName) {
        switch (action) {
          case "removeBackground":
            assetName = `photoshop__${key}__${crypto.randomUUID()}`;
            break;
          default:
            assetName = `firefly__${key}__${crypto.randomUUID()}`;
            break;
        }
      }

      if (assetData.filePath && assetData.presignURL) {
        queryClient.setQueryData(s3Key.presignURL(assetData.filePath), {
          filePath: assetData.filePath,
          presignURL: assetData.presignURL,
        });
      }

      return uploadAssets.mutateAsync({
        assets: [
          {
            name: assetName,
            type: isVideoModel ? "video" : "image",
            src: assetData.url,
            ...(assetData.filePath ? { filePath: assetData.filePath } : {}),
            ...(assetData.presignURL
              ? { presignURL: assetData.presignURL }
              : {}),
            skipClientPrepare: true,
            referenceKey,
            fireflyId,
            seed: assetData.seed,
            model,
            prompt,
            ...(sourceValue ? { sourceValue } : {}),
            ...(sourceReferences ? { sourceReferences } : {}),
          },
        ],
        fileName: key,
        key,
      });
    },
    onError: async (error, variables, onMutateResult, { meta, client }) => {
      //TODO: Set default image for the variation in case of error
      console.warn("Error generating image variation:", error, variables);

      stopGenerating(variables.key);
      store.getState().setImageVariationProgress(variables.key, null);

      if (!!error?.cause?.data?.error_code) {
        const template = client.getQueryData(
          videoKey.template(videoData?.templateId),
        );

        if (error?.cause?.data?.error_code === "prompt_unsafe") {
          const _variation = template?.imageVariations?.find(
            (v) => v.key === variables.key,
          );
          meta.errorMessage = `${!!_variation && !!_variation.label ? `[${_variation.key}] ${_variation.label}` : variables.key}: The generated prompt was flagged as unsafe. Please try again. If the issue persists, please edit the prompt and try again.`;
        }

        const failed = videoData?.imageVariations?.find(
          (v) => v.key === variables.key,
        );

        // Through the same queue as every other `imageVariations` write, so
        // flagging the failure cannot drop a prompt written in the meantime.
        if (failed) {
          await persistImageVariations([
            {
              ...failed,
              errorCode: error?.cause?.data?.error_code,
              errorMessage: error?.cause?.data?.message,
            },
          ]);
        }
      }

      if (error)
        if (variables?.referenceKey) {
          const defaultAsset = videoData?.assets?.find(
            (asset) => asset.name === variables.referenceKey,
          );
          if (defaultAsset) {
            updateAssets.mutate({
              assets: [{ ...defaultAsset, name: variables.key }],
            });
          }
        }
    },
    meta: {
      errorMessage: "Failed to generate image variation. Please try again.",
    },
  });

  const { mutate: mutateImageVariation } = imageVariation;

  // Generate image variations
  useEffect(() => {
    if (!isActive) {
      return;
    }

    /** Regenerate this variation even though it already has an image. */
    const isForced = (key) => forcedKeys.has(key);

    // The forced keys this pass is done with: started, or impossible to.
    const consumed = new Set();

    // Static prompts refreshed for this pass, persisted once it is done.
    const refreshedPrompts = [];

    const hasPendingVariation = (v) =>
      eligibleKeys.has(v.key) &&
      !!v.visualPrompt &&
      (isForced(v.key) ||
        (!v.errorCode &&
          !videoData?.assets?.some((asset) => asset.name === v.key))) &&
      !generatingKeysRef.current.has(v.key);

    if (
      // Every reference of the step is uploaded together, so generation waits
      // for all of them — starting a variation whose upload is still running
      // would generate it without its reference image.
      !references.isFetching &&
      !references.isError &&
      !uploadAssets.isPending &&
      !writer.isPending &&
      videoData?.imageVariations?.some?.(hasPendingVariation)
    ) {
      // If there are image variations that haven't been uploaded yet, trigger
      // their generation.
      videoData?.imageVariations
        ?.filter(hasPendingVariation)
        ?.forEach((variation) => {
          const templateVariation =
            templateData?.imageVariations?.find(
              (tv) => tv.key === variation.key,
            ) ?? {};

          // Every reference image of this variation, primary first.
          // Generation waits until all of them exist — starting with a
          // half-ready set would silently drop references from the result.
          const variationReferences = getReferenceAssets(templateVariation);

          if (
            variationReferences.some(
              (asset) => isReferenceExpected(asset.name) && !asset?.filePath,
            )
          ) {
            return;
          }

          const uploadIds = resolveVariationUploadIds(templateVariation, {
            byKey: references.byKey,
            isReferenceExpected,
          });

          if (uploadIds === null) {
            return;
          }

          const promptContext = {
            templateId: videoData?.templateId,
            campaign,
            customer: videoData?.customer,
            visual: variationReferences[0],
            visuals: variationReferences,
          };

          // A static prompt is generated from the campaign as it is now —
          // unless it is the user's own, which is kept as written.
          const staticPrompt = variation.promptEdited
            ? null
            : resolveStaticPrompt(templateVariation, promptContext);

          let promptVariation =
            staticPrompt !== null && staticPrompt !== variation.visualPrompt
              ? { ...variation, visualPrompt: staticPrompt }
              : variation;

          if (promptVariation !== variation) {
            refreshedPrompts.push(promptVariation);
          }

          // The user's own prompt is stored as typed, and has its `{{Var}}`
          // placeholders expanded from the campaign only for this request.
          if (variation.promptEdited) {
            const compiledPrompt = compileUserPrompt(
              variation.visualPrompt,
              templateVariation,
              promptContext,
            );

            if (compiledPrompt === null) {
              console.warn(
                `The prompt of "${variation.key}" has no campaign values to fill it with.`,
              );
              consumed.add(variation.key);
              return;
            }

            promptVariation = { ...variation, visualPrompt: compiledPrompt };
          }

          // What the variation's type asks for — model, prompt, references,
          // action — all of it resolved from its entry in `VARIATION_TYPES`.
          const payload = buildVariationPayload(promptVariation, {
            templateVariation,
            references: variationReferences,
            uploadIds,
          });

          consumed.add(variation.key);

          if (payload) {
            mutateImageVariation({
              ...payload,
              // What the image is generated from, recorded on it so a view can
              // tell when it has changed since: the campaign values it depends
              // on — generating it is what catches up with them — and the
              // files of its references.
              sourceValue: snapshotDependencies(
                getVariationDependencies(
                  templateVariation,
                  sourceDependencies?.get(variation.key),
                ),
                campaign,
              ),
              sourceReferences: snapshotReferences(
                getVariationReferenceKeys(templateVariation),
                videoData?.assets,
              ),
            });
          }
        });

      // So the variation shows the prompt its image is generated from.
      if (refreshedPrompts.length) {
        persistImageVariations(refreshedPrompts).catch((error) => {
          console.warn("Failed to save the refreshed prompts:", error);
        });
      }

      // A forced key still waiting — on its references, say — stays forced.
      if ([...consumed].some(isForced)) {
        setForced((prev) => {
          const keys = new Set(prev.keys);

          consumed.forEach((key) => keys.delete(key));

          return { ...prev, keys };
        });
      }
    }
  }, [
    isActive,
    references.isFetching,
    references.isError,
    references.byKey,
    uploadAssets.isPending,
    writer.isPending,
    videoData?.imageVariations,
    videoData?.assets,
    templateData?.imageVariations,
    eligibleKeys,
    getReferenceAssets,
    isReferenceExpected,
    mutateImageVariation,
    forcedKeys,
    sourceDependencies,
    campaign,
    persistImageVariations,
    videoData?.templateId,
    videoData?.customer,
    runToken,
  ]);

  // ---------------------------------------------------------------------------
  // Step-action surface
  // ---------------------------------------------------------------------------

  /**
   * The variations of this step that still have to be generated — a failed one
   * is not pending, because it only regenerates when explicitly asked for.
   */
  const pendingVariations = useStable(
    useMemo(
      () =>
        stageVariations.filter((variation) => {
          const data = videoData?.imageVariations?.find(
            (v) => v.key === variation.key,
          );

          return (
            !videoData?.assets?.some((asset) => asset.name === variation.key) &&
            !data?.errorCode
          );
        }),
      [stageVariations, videoData?.imageVariations, videoData?.assets],
    ),
  );

  /** A pending variation is still waiting for a reference image of its own. */
  const isReferenceMissing = useMemo(
    () =>
      pendingVariations.some((variation) =>
        getReferenceAssets(variation).some(
          (asset) => isReferenceExpected(asset.name) && !asset?.filePath,
        ),
      ),
    [pendingVariations, getReferenceAssets, isReferenceExpected],
  );

  /**
   * Arms the pipeline. Safe to call repeatedly — every stage of the pipeline
   * guards itself, so entering the step again never duplicates work.
   *
   * @param {object} [options]
   * @param {boolean} [options.force] - Regenerate variations that already have
   *   an image, not just the missing ones.
   * @param {Iterable<string>} [options.keys] - Limit `force` to these variation
   *   keys — a single reference group, say. Every variation of the step when
   *   omitted.
   */
  const run = useCallback(
    ({ force = false, keys } = {}) => {
      if (force) {
        const forcedKeys = Array.from(keys ?? stageKeys).filter((key) =>
          stageKeys.has(key),
        );

        setForced((prev) => {
          const next = new Set(
            prev.projectId === projectId ? prev.keys : undefined,
          );

          forcedKeys.forEach((key) => next.add(key));

          return { projectId, keys: next };
        });
      }

      retryLabeling();
      setArmedProjectId(projectId ?? null);
      setRunToken((token) => token + 1);
    },
    [projectId, stageKeys, retryLabeling],
  );

  /**
   * Asks for never-generated variations — what a `manual` pipeline waits for —
   * and arms the pipeline. Without a `manual` pipeline, it is just `run()`.
   *
   * @param {Iterable<string>} [keys] - The variations to generate. Every
   *   variation of the step when omitted.
   */
  const generate = useCallback(
    (keys) => {
      const requestedKeys = Array.from(keys ?? stageKeys).filter((key) =>
        stageKeys.has(key),
      );

      setRequested((prev) => {
        const next = new Set(
          prev.projectId === projectId ? prev.keys : undefined,
        );

        requestedKeys.forEach((key) => next.add(key));

        return { projectId, keys: next };
      });

      run();
    },
    [projectId, stageKeys, run],
  );

  /**
   * Catches outdated variations up at once, each as its `action` asks (see
   * `getOutdatedAction`): `"prompt-and-image"` drops its prompt — the user's
   * own included — so a fresh one is written, `"image"` keeps it. Either way
   * its images go, so the armed pipeline generates them again; a previous
   * failure is cleared too. One write for all of them, as
   * `useImageVariation`'s `regenerate` / `regenerateFromCampaign` do for one.
   *
   * @param {Array<{ key: string, action: string }>} updates
   * @returns {Promise<string[]>} The keys being regenerated.
   */
  const updateVariations = useCallback(
    async (updates) => {
      const latest = queryClient.getQueryData(videoKey.detail(projectId));

      const targets = (updates ?? [])
        .filter(
          ({ key, action }) =>
            stageKeys.has(key) &&
            !!action &&
            !generatingKeysRef.current.has(key),
        )
        .map(({ key, action }) => ({
          key,
          action,
          variation: stageVariations.find((v) => v.key === key),
          record: latest?.imageVariations?.find((v) => v.key === key),
          asset: latest?.assets?.find((a) => a.name === key),
        }));

      if (!targets.length) {
        return [];
      }

      const prompts = targets
        .filter(({ action }) => action === "prompt-and-image")
        .map(({ key, variation, record }) => {
          const { errorCode, errorMessage, promptEdited, ...rest } = record ?? {
            type: variation?.type,
            referenceKey: getPrimaryReferenceKey(variation),
          };

          return { ...rest, key, visualPrompt: null };
        });

      const images = targets
        .filter(({ action, record }) => action !== "prompt-and-image" && record)
        .map(({ key, record }) => {
          const { errorCode, errorMessage, ...rest } = record;

          return { ...rest, key };
        });

      try {
        await persistImageVariations([...prompts, ...images]);
      } catch (error) {
        // Keep the current images rather than dropping them for a generation
        // that would run with the old prompts.
        console.warn("Failed to update the variations:", error);
        return [];
      }

      const toRemove = targets
        .flatMap(({ asset }) => [
          asset?.name,
          getPrimaryReferenceKey({ referenceKey: asset?.referenceKey }),
        ])
        .filter(Boolean)
        .map((name) => ({ name }));

      if (toRemove.length) {
        updateAssets.mutate({ toRemove });
      }

      run();

      return targets.map(({ key }) => key);
    },
    [
      queryClient,
      projectId,
      stageKeys,
      stageVariations,
      persistImageVariations,
      updateAssets.mutate,
      run,
    ],
  );

  /**
   * Regenerates — prompt and image — the variations of the step whose source
   * an edit of the campaign changed (see `getCampaignChangedVariationKeys`).
   * Only ones that already have a prompt or an image are concerned — the rest
   * are written for the edited campaign anyway — and neither an image the user
   * uploaded nor one being generated is touched.
   *
   * One write for all of them, as `useImageVariation.regenerateFromCampaign`
   * does for one: their prompts are dropped so fresh ones are written, then
   * their images, so the armed pipeline generates them again.
   *
   * @returns {Promise<string[]>} The keys being regenerated.
   */
  const regenerateFromCampaignChange = useCallback(
    async (previousCampaign, nextCampaign) => {
      const latest = queryClient.getQueryData(videoKey.detail(projectId));

      const changedKeys = new Set(
        getCampaignChangedVariationKeys(
          stageVariations,
          previousCampaign,
          nextCampaign,
          {
            structureDependencies: sourceDependencies,
            getPromptTemplate: (variation, campaign) => {
              const visuals = resolveReferenceAssets(latest?.assets, variation);

              return getVariationPromptTemplate(variation, {
                templateId: latest?.templateId,
                campaign,
                customer: latest?.customer,
                visual: visuals[0],
                visuals,
              });
            },
          },
        ),
      );

      const targets = stageVariations
        .filter(
          (variation) =>
            changedKeys.has(variation.key) &&
            !generatingKeysRef.current.has(variation.key),
        )
        .map((variation) => ({
          variation,
          record: latest?.imageVariations?.find((v) => v.key === variation.key),
          asset: latest?.assets?.find((a) => a.name === variation.key),
        }))
        .filter(
          ({ record, asset }) =>
            (!!record?.visualPrompt || !!asset?.filePath) &&
            !isUploadedImage(asset),
        );

      if (!targets.length) {
        return [];
      }

      try {
        await persistImageVariations(
          targets.map(({ variation, record }) => {
            const { errorCode, errorMessage, ...rest } = record ?? {
              type: variation.type,
              referenceKey: getPrimaryReferenceKey(variation),
            };

            return { ...rest, key: variation.key, visualPrompt: null };
          }),
        );
      } catch (error) {
        // Keep the current images rather than dropping them for a generation
        // that would run with the old prompts.
        console.warn("Failed to request new prompts:", error);
        return [];
      }

      const toRemove = targets
        .flatMap(({ asset }) => [
          asset?.name,
          getPrimaryReferenceKey({ referenceKey: asset?.referenceKey }),
        ])
        .filter(Boolean)
        .map((name) => ({ name }));

      if (toRemove.length) {
        updateAssets.mutate({ toRemove });
      }

      run();

      return targets.map(({ variation }) => variation.key);
    },
    [
      queryClient,
      projectId,
      stageVariations,
      sourceDependencies,
      persistImageVariations,
      updateAssets.mutate,
      run,
    ],
  );

  return useMemo(
    () => ({
      stages,
      groups,
      variations: stageVariations,
      pendingVariations,
      referenceAssets,
      referenceUrlByPath: references.urlByPath,
      generatingKeys,
      idleKeys,
      skipImageLabeling: labeling.skipImageLabeling,
      isArmed,
      isComplete: !pendingVariations.length,
      isReferenceMissing,
      isUploading: uploadAssets.isPending,
      isSaving: writer.isPending,
      references,
      labeling,
      prompts,
      run,
      generate,
      regenerateFromCampaignChange,
      updateVariations,
    }),
    [
      stages,
      groups,
      stageVariations,
      pendingVariations,
      referenceAssets,
      references,
      generatingKeys,
      idleKeys,
      labeling,
      prompts,
      isArmed,
      isReferenceMissing,
      uploadAssets.isPending,
      writer.isPending,
      run,
      generate,
      regenerateFromCampaignChange,
      updateVariations,
    ],
  );
};
